import {
  LIBRARY_ATTACHMENTS,
  LIBRARY_OBJECTS,
  LIBRARY_ROBOTS,
} from "../../utils/libraryCatalog.js";
import type { Body, WorldBody } from "../../types/scene";
import { getSceneState } from "../../stores/sceneStore";
import { useSceneBehaviorContextStore } from "../../stores/sceneBehaviorContextStore";

export type SceneBehaviorContext = {
  recipeId: string | null;
  scenarioId: string | null;
  recipeRobotId: string | null;
  worldBodyCount: number;
  isSceneEmpty: boolean;
  /** First matching library robot id from root world bodies (e.g. `unitree_go2`). */
  primaryRobotLibraryId: string | null;
  robotLibraryIds: string[];
  /** Locomotion policy palette filter: quadruped id when exactly one locomotion robot is primary, else null. */
  locomotionRobotLibraryId: string | null;
  /** True when scene shape satisfies the trained locomotion model contract (base + 12 scalar joints + 12 joint actuators). */
  supportsLocoPolicyContract: boolean;
  gripperLibraryId: string | null;
  hasManipulationGripper: boolean;
  cubeBodyUuid: string | null;
  trayBodyUuid: string | null;
};

const LOCOMOTION_ROBOT_IDS = new Set(["unitree_go2", "boston_dynamics_spot"]);

function normLabel(value: string) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function worldBodyUuid(wb: WorldBody | string): string {
  return typeof wb === "string" ? wb : wb.uuid;
}

function worldBodyLabel(wb: WorldBody | string, bodies: Record<string, Body>): string {
  if (typeof wb === "string") return bodies[wb]?.name || "";
  const fromMap = bodies[wb.uuid]?.name;
  return (fromMap && String(fromMap)) || wb.name || "";
}

function inferLibraryRobotId(worldName: string): string | null {
  const n = normLabel(worldName);
  if (!n) return null;
  for (const robot of LIBRARY_ROBOTS) {
    const rid = normLabel(robot.id.replace(/_/g, " "));
    const rname = normLabel(robot.name);
    if (n === rname || (rid.length > 2 && n.includes(rid))) {
      return robot.id;
    }
  }
  if (n.includes("go2") || (n.includes("unitree") && n.includes("go"))) {
    return "unitree_go2";
  }
  if (n.includes("spot")) {
    return "boston_dynamics_spot";
  }
  return null;
}

function inferGripperLibraryId(worldName: string): string | null {
  const n = normLabel(worldName);
  if (!n) return null;
  for (const g of LIBRARY_ATTACHMENTS) {
    const gname = normLabel(g.name);
    const gid = normLabel(g.id.replace(/_/g, " "));
    if (n.includes(gname) || (gid.length > 2 && n.includes(gid))) {
      return g.id;
    }
  }
  if (
    n.includes("robotiq") ||
    n.includes("robotique") ||
    n.includes("2f-85") ||
    n.includes("2f85")
  ) {
    return "robotiq_2f85";
  }
  return null;
}

function inferObjectLibraryId(worldName: string): string | null {
  const n = normLabel(worldName);
  if (!n) return null;
  for (const obj of LIBRARY_OBJECTS) {
    if (normLabel(obj.name) === n) {
      return obj.id;
    }
  }
  if (n === "cube" || n.endsWith(" cube") || n.startsWith("cube ")) return "cube";
  if (n === "tray" || n.endsWith(" tray") || n.startsWith("tray ")) return "tray";
  return null;
}

function inferLocomotionRobotFromJointNames(
  joints: Record<string, { name?: string }>
): string | null {
  const names = Object.values(joints || {})
    .map((j) => normLabel(j?.name || ""))
    .filter(Boolean);
  if (!names.length) return null;

  // Spot mjcf joint naming typically uses fl_hx / fr_hx / hl_hx / hr_hx (+ hy/kn variants).
  const hasSpotSignature =
    names.some((n) => n === "fl_hx") &&
    names.some((n) => n === "fr_hx") &&
    names.some((n) => n === "hl_hx") &&
    names.some((n) => n === "hr_hx");
  if (hasSpotSignature) return "boston_dynamics_spot";

  // Go2 mjcf joint naming typically uses FL_hip_joint ... RR_calf_joint.
  const hasGo2Signature =
    names.some((n) => n === "fl_hip_joint") &&
    names.some((n) => n === "fr_hip_joint") &&
    names.some((n) => n === "rl_hip_joint") &&
    names.some((n) => n === "rr_hip_joint");
  if (hasGo2Signature) return "unitree_go2";

  return null;
}

function inferLocomotionRobotFromActuatorKinds(
  actuators: Record<string, { kind?: string; joint?: string; tendonUuid?: string | null }>
): string | null {
  const jointActuators = Object.values(actuators || {}).filter((a) => {
    const hasJoint = typeof a?.joint === "string" && a.joint.length > 0;
    const hasTendon = typeof a?.tendonUuid === "string" && a.tendonUuid.length > 0;
    return hasJoint && !hasTendon;
  });
  if (jointActuators.length < 8) return null;

  const motorCount = jointActuators.filter((a) => a?.kind === "motor").length;
  const positionCount = jointActuators.filter((a) => a?.kind === "position").length;

  // Go2 locomotion assets are torque motor-driven; Spot assets are position-actuator driven.
  if (motorCount >= 8 && motorCount > positionCount) return "unitree_go2";
  if (positionCount >= 8 && positionCount > motorCount) return "boston_dynamics_spot";
  return null;
}

function inferSupportsLocoPolicyContract(scene: {
  joints?: Record<string, { kind?: string }>;
  actuators?: Record<string, { joint?: string; tendonUuid?: string | null }>;
}): boolean {
  const joints = Object.values(scene.joints || {});
  const actuators = Object.values(scene.actuators || {});
  const freeJointCount = joints.filter((j) => j?.kind === "free").length;
  const scalarJointCount = joints.filter((j) => j?.kind === "hinge" || j?.kind === "slide").length;
  const jointActuatorCount = actuators.filter((a) => {
    const hasJoint = typeof a?.joint === "string" && a.joint.length > 0;
    const hasTendon = typeof a?.tendonUuid === "string" && a.tendonUuid.length > 0;
    return hasJoint && !hasTendon;
  }).length;
  return freeJointCount >= 1 && scalarJointCount >= 12 && jointActuatorCount >= 12;
}

/**
 * Snapshot of scene + last-loaded preset metadata for behavior editor rules.
 * Call from React hooks or actions (reads current Zustand scene + recipe stores).
 */
export function buildSceneBehaviorContext(): SceneBehaviorContext {
  const scene = getSceneState();
  const bodies = scene.bodies || {};
  const worldBodies = (scene.worldBodies || []) as WorldBody[];
  const joints = (scene.joints || {}) as Record<string, { name?: string; kind?: string }>;
  const actuators = (scene.actuators || {}) as Record<
    string,
    { kind?: string; joint?: string; tendonUuid?: string | null }
  >;

  const recipe = useSceneBehaviorContextStore.getState();

  let primaryRobotLibraryId: string | null = null;
  let gripperLibraryId: string | null = null;
  const robotHits = new Set<string>();
  const objectHits: Record<string, string | null> = { cube: null, tray: null };

  for (const wb of worldBodies) {
    const uuid = worldBodyUuid(wb);
    const label = worldBodyLabel(wb, bodies);
    const robotId = inferLibraryRobotId(label);
    if (robotId && !primaryRobotLibraryId) {
      primaryRobotLibraryId = robotId;
    }
    if (robotId) robotHits.add(robotId);
    const gId = inferGripperLibraryId(label);
    if (gId && !gripperLibraryId) {
      gripperLibraryId = gId;
    }
    const objId = inferObjectLibraryId(label);
    if (objId === "cube" && !objectHits.cube) objectHits.cube = uuid;
    if (objId === "tray" && !objectHits.tray) objectHits.tray = uuid;
  }

  if (!primaryRobotLibraryId && recipe.recipeRobotId) {
    primaryRobotLibraryId = recipe.recipeRobotId;
  }
  if (recipe.recipeRobotId) {
    robotHits.add(recipe.recipeRobotId);
  }

  const jointSignatureRobotId = inferLocomotionRobotFromJointNames(joints);
  const actuatorKindRobotId = inferLocomotionRobotFromActuatorKinds(actuators);
  if (actuatorKindRobotId) {
    robotHits.add(actuatorKindRobotId);
    if (!primaryRobotLibraryId) {
      primaryRobotLibraryId = actuatorKindRobotId;
    }
  }
  if (jointSignatureRobotId) {
    robotHits.add(jointSignatureRobotId);
    if (!primaryRobotLibraryId) {
      primaryRobotLibraryId = jointSignatureRobotId;
    }
  }

  const locomotionCandidates = worldBodies
    .map((wb) => inferLibraryRobotId(worldBodyLabel(wb, bodies)))
    .filter((id): id is string => Boolean(id && LOCOMOTION_ROBOT_IDS.has(id)));

  let locomotionRobotLibraryId: string | null = null;
  if (locomotionCandidates.length === 1) {
    locomotionRobotLibraryId = locomotionCandidates[0]!;
  } else if (locomotionCandidates.length > 1) {
    locomotionRobotLibraryId = null;
  } else if (actuatorKindRobotId && LOCOMOTION_ROBOT_IDS.has(actuatorKindRobotId)) {
    locomotionRobotLibraryId = actuatorKindRobotId;
  } else if (jointSignatureRobotId && LOCOMOTION_ROBOT_IDS.has(jointSignatureRobotId)) {
    locomotionRobotLibraryId = jointSignatureRobotId;
  } else if (recipe.recipeRobotId && LOCOMOTION_ROBOT_IDS.has(recipe.recipeRobotId)) {
    locomotionRobotLibraryId = recipe.recipeRobotId;
  }

  const hasManipulationGripper =
    gripperLibraryId === "robotiq_2f85" ||
    robotHits.has("franka_emika_panda") ||
    robotHits.has("trossen_wx250s");
  const supportsLocoPolicyContract = inferSupportsLocoPolicyContract({ joints, actuators });

  return {
    recipeId: recipe.recipeId,
    scenarioId: recipe.scenarioId,
    recipeRobotId: recipe.recipeRobotId,
    worldBodyCount: worldBodies.length,
    isSceneEmpty: worldBodies.length === 0,
    primaryRobotLibraryId,
    robotLibraryIds: Array.from(robotHits),
    locomotionRobotLibraryId,
    supportsLocoPolicyContract,
    gripperLibraryId,
    hasManipulationGripper,
    cubeBodyUuid: objectHits.cube,
    trayBodyUuid: objectHits.tray,
  };
}
